import { Company } from "../nextcargo";

/**
 * Parâmetros genéricos que podem ser definidos para cada endpoint
 * Usado como base para definir tipos de API
 */
export type GenParams = {
  args: unknown;
  result: unknown;
  raw: unknown;
  /* [key: string]: unknown; */
};

/**
 * Tipo genérico para definir a estrutura completa de tipos de uma API
 * Valida que todos os endpoints da API tenham seus tipos definidos
 *
 * @template T - A API que contém os endpoints (ex: typeof authApi)
 * @template K - Objeto que mapeia cada endpoint para seus tipos (args, result, raw)
 *
 * @example
 * type Auth = GenEndpoint<
 *   typeof authApi,
 *   {
 *     login: {
 *       args: { email: string; password: string };
 *       result: { token: string; user: User };
 *     };
 *     logout: {
 *       args: void;
 *       result: void;
 *     };
 *   }
 * >;
 */
export type GenEndpoint<
  T extends { endpoints: Record<string, unknown> } | string,
  K extends Record<T extends { endpoints: Record<infer EndpointName extends string, unknown> } ? EndpointName : T extends string ? T : never, Partial<GenParams> | undefined>
> = K;

/**
 * Tipo genérico para definir tipos de API usando uma lista de nomes de endpoints
 * Esta versão evita referências circulares ao usar string literals em vez de typeof
 *
 * @template E - Lista de nomes de endpoints como string literals (ex: "login" | "logout")
 * @template K - Objeto que mapeia cada endpoint para seus tipos (args, result, raw)
 *
 * @example
 * type Auth = GenEndpointByNames<
 *   "login" | "test",
 *   {
 *     login: {
 *       args: { email: string; password: string };
 *       result: { token: string; user: User };
 *     };
 *     test: undefined;
 *   }
 * >;
 */
export type GenEndpointByNames<E extends string, K extends Record<E, Partial<GenParams> | undefined>> = K;

/**
 * Tipo helper para validar que um tipo de API corresponde aos endpoints definidos
 * Use este tipo DEPOIS que ambos o tipo da API e a instância da API estão definidos
 * para validar a correspondência sem causar referência circular
 *
 * @template T - O tipo da API (ex: Auth)
 * @template A - A instância da API (ex: typeof authApi)
 *
 * @example
 * // No final do arquivo auth.api.ts após a definição do authApi:
 * type _ValidateAuth = ValidateEndpointTypes<Auth, typeof authApi>;
 * // Se Auth não corresponder aos endpoints de authApi, haverá um erro de tipo
 */
export type ValidateEndpointTypes<T extends Record<string, Partial<GenParams> | undefined | object>, A extends { endpoints: Record<keyof T, unknown> }> = T & { __validated: A };

/**
 * Tipo genérico para extrair uma propriedade específica (args, result ou raw) de todos os endpoints de uma API
 * Retorna um Record onde cada endpoint contém apenas a propriedade selecionada
 *
 * @template T - A API que contém os endpoints (ex: typeof authApi)
 * @template K - A propriedade a ser extraída ("args" | "result" | "raw")
 *
 * @example
 * // Extrai apenas os tipos de args de todos os endpoints
 * type AuthArgs = PickEndpoint<typeof authApi, "args">;
 * // Resultado: { login: { args: LoginArgs }, logout: { args: void } }
 *
 * @example
 * // Extrai apenas os tipos de result de todos os endpoints
 * type AuthResults = PickEndpoint<typeof authApi, "result">;
 * // Resultado: { login: { result: LoginResult }, logout: { result: void } }
 */
export type PickEndpoint<T extends { endpoints: Record<string, unknown> }, K extends keyof GenParams> = Record<keyof T["endpoints"], Pick<GenParams, K>>;

/**
 * Helper type para pegar um tipo específico de um endpoint
 * @template T - O tipo da API (ex: Auth)
 * @template E - O nome do endpoint (ex: "login")
 * @template K - A propriedade desejada (ex: "args" | "result" | "raw")
 *
 * @example
 * type LoginArgs = PickEndpointType<Auth, "login", "args">;
 * type LoginResult = PickEndpointType<Auth, "login", "result">;
 */
export type PickEndpointType<T extends Record<string, Partial<GenParams> | undefined | object>, E extends keyof T, K extends keyof GenParams> =
  T[E] extends Partial<GenParams> ? (T[E][K] extends undefined ? never : T[E][K]) : never;

/**
 * Helper type para pegar os args de um endpoint específico
 * @template T - O tipo da API (ex: Auth)
 * @template E - O nome do endpoint (ex: "login")
 *
 * @example
 * type LoginArgs = PickEndpointArgs<Auth, "login">;
 */
export type PickEndpointArgs<T extends Record<string, Partial<GenParams> | undefined | object>, E extends keyof T> = PickEndpointType<T, E, "args">;

/**
 * Helper type para pegar o result de um endpoint específico
 * @template T - O tipo da API (ex: Auth)
 * @template E - O nome do endpoint (ex: "login")
 *
 * @example
 * type LoginResult = PickEndpointResult<Auth, "login">;
 */
export type PickEndpointResult<T extends Record<string, Partial<GenParams> | undefined | object>, E extends keyof T> = PickEndpointType<T, E, "result">;

/**
 * Helper type para pegar o raw de um endpoint específico
 * @template T - O tipo da API (ex: Auth)
 * @template E - O nome do endpoint (ex: "login")
 *
 * @example
 * type LoginRaw = PickEndpointRaw<Auth, "login">;
 */
export type PickEndpointRaw<T extends Record<string, Partial<GenParams> | undefined | object>, E extends keyof T> = PickEndpointType<T, E, "raw">;

/**
 * Enum representing the different user roles in the system.
 *
 * - Administrador: Has full administrative permissions.
 * - Expedicao: Responsible for shipping/dispatch operations.
 * - Usuario: Regular system user.
 * - Transportadora: Represents carrier/partner users.
 * - Espectador: Has view-only access.
 * - Portaria: Users responsible for access control/gatekeeping.
 */
export enum Role {
  Administrador = "Administrador",
  Expedicao = "Expedição",
  Usuario = "Usuario",
  Transportadora = "Transportadora",
  Espectador = "Espectador",
  Portaria = "Portaria"
}

/**
 * Represents a user session within the system.
 *
 * @property {number} id - Unique identifier for the session/user.
 * @property {number} shippingCompanyId - The ID of the associated shipping company.
 * @property {Company} company - The company object related to the user.
 * @property {Role} role - The current user's role in the system.
 * @property {string} user - The username or user identifier.
 * @property {string | null} token - The authentication token, or null if not set.
 * @property {boolean} isAuthenticated - Indicates if the user is authenticated.
 */
export type Session = {
  id: number;
  shippingCompanyId: number;
  company: Company;
  role: Role;
  user: string;
  token: string | null;
  isAuthenticated: boolean;
  isRoot: boolean;
  logo?: string;
};
